import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { AppSettingService } from '../app.service';
import { UserInfoService } from './user-info.service';

@Injectable({
	providedIn: 'root'
})
export class AccountAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "Account";

	getBasicInfo(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/GetBasicInfo",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	updateFullInfo(data: any): Rx.Observable<any> {
		return this.http
			.put<any>(
				this.url + "/UpdateFullInfo",
				data,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			);
	}

	updateAuthentication(data: any): Rx.Observable<any> {
		return this.http.put<any>(
			this.url + "/UpdateAuthentication",
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	};
	
	createConnectionCode(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/CreateConnectionCode",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getConnectionCode(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/GetConnectionCode",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	private myProfileCahe = {
		personGuid: "" as string,
		cache: null as any
	};

	updateMyProfile(profile: any): Rx.Observable<any> {
		this.myProfileCahe.cache = null;
		return this.http.put<any>(
			this.url + "/Profile",
			profile,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	initMyProfile(): Promise<any> {
		if (this.userInfo == null || this.userInfo.isAnonymous()) {
			return new Promise((resolve) => {
				resolve(null);
			});
		}
		else {
			// ensure we do not throw error
			return Rx.firstValueFrom(this.getMyProfile())
				.catch((error: any) => {
					console.log("initMyProfile error: ", error);
					// if we cannot get profile by existing credential, force to log off
					this.userInfo.clearProfile(true);
				}).then(() => { });
		}
	}

	getMyProfile(): Rx.Observable<any> {
		if (this.userInfo.getProfile().PersonGuid !== this.myProfileCahe.personGuid) {
			this.myProfileCahe.cache = null;
		}

		if (this.myProfileCahe.cache != null) {
			return Rx.of(this.myProfileCahe.cache);
		}
		else {
			return this.http.get<any>(
				this.url + "/Profile",
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(tap(
				response => {
					// add "assets" to leading
					if (response.Photo.toLowerCase().indexOf("/images/") === 0) {
						response.Photo = "assets" + response.Photo;
					}
					if (response.CoverPhoto.toLowerCase().indexOf("/images/") === 0) {
						response.CoverPhoto = "assets" + response.CoverPhoto;
					}
					this.myProfileCahe.personGuid = this.userInfo.getProfile().PersonGuid;
					this.myProfileCahe.cache = response;
				}
			));
		}
	}

	getPhotoDefaultList(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Profile/GetPhotoDefaultList",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(tap(
			response => {
				response.map((x: any) => {
					x.Path = "assets" + x.Path;
					return x;
				});
			}
		));
	}

	getCoverPhotoDefaultList(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Profile/GetCoverPhotoDefaultList",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(tap(
			response => {
				response.map((x: any) => {
					x.Path = "assets" + x.Path;
					return x;
				});
			}
		));
	}

	searchDreamJob(title: string, limit:number, offset:number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Profile/SearchDreamJob?kw=" + encodeURIComponent(title) + "&limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deleteDreamJob(): Rx.Observable<any> {
		this.myProfileCahe.cache = null;
		return this.http.get<any>(
			this.url + "/Profile/DeleteDreamJob",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getRoleModelSuggestList(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Profile/GetRoleModelSuggestList",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getMyProgress(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/MyProgress",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}


@Injectable({
	providedIn: 'root'
})
export class VisitHistoryAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "VisitHistory";

	getRecent(offset: number, limit: number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Recent?limit=' + limit.toString() + '&offset=' + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	createHistoryEntry(historyEntry: any): Rx.Observable<any> {
		return this.http.post<any>(
			this.url,
			historyEntry,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	clearHistory(): Rx.Observable<any> {
		return this.http.patch<any>(
			this.url + '/ClearHistory',
			null,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}

export interface IProfessionalHistory {
	HistoryId: string,
	OnetCode: string,
	OccupationName: string,
	CareerId: string,
	CompanyName: string,
	City: string,
	State: string,
	StartDate: string,
	EndDate: string,
	CurrentAttend: boolean,
	RegularJob: boolean,
	TotalMinute: number,
	TotalHour: string
}

export class ProfessionalHistory implements IProfessionalHistory {
	HistoryId = "";
	OnetCode = "";
	OccupationName = "";
	CareerId = "";
	CompanyName = "";
	City = "";
	State = "";
	StartDate: string = null;
	EndDate: string = null;
	CurrentAttend: boolean = true;
	RegularJob: boolean = true;
	TotalMinute: number = 0;
	TotalHour: string = "";

	constructor(isRegular: boolean) {
		this.RegularJob = isRegular;
	}

	reset() {
		this.HistoryId = "";
		this.OnetCode = "";
		this.OccupationName = "";
		this.CareerId = "";
		this.CompanyName = "";
		this.City = "";
		this.State = "";
		this.StartDate = null;
		this.EndDate = null;
		this.CurrentAttend = true;
		this.TotalMinute = 0;
		this.TotalHour = "";
	}
}

export interface IProfessionalHistoryHour {
	JobDate: string,
	FromTime: string,
	ToTime: string,
	TotalMinute: number
}

export class ProfessionalHistoryHour implements IProfessionalHistoryHour {
	JobDate = "";
	FromTime = "";
	ToTime = "";
	TotalMinute = 0;
}

@Injectable({
	providedIn: 'root'
})
export class ProfessionalHistoryAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }

	private url = AppSettingService.appConfig.JourneyAPIUrl + "ProfessionalHistory";
	
	getHistories(regularJob: boolean, userId: string, limit: number, offset: number): Rx.Observable<IProfessionalHistory[]> {
		return this.http.get<any>(
			this.url + "?regularJob=" + regularJob.valueOf() + "&userId=" + encodeURIComponent(userId) + "&limit=" + limit.toString() + "&offset=" + offset.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveHistory(job: IProfessionalHistory): Rx.Observable<IProfessionalHistory> {
		return this.http.post<any>(
			this.url,
			job,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getHistory(historyId: number): Rx.Observable<IProfessionalHistory> {
		return this.http.get<any>(
			this.url + "/" + historyId.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deleteHistory(historyId: number): Rx.Observable<any> {
		return this.http.delete<any>(
			this.url + "/" + historyId.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getHistoryHour(historyId: number): Rx.Observable<IProfessionalHistoryHour[]> {
		return this.http.get<any>(
			this.url + "/" + + historyId.toString() + "/Hour",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveHistoryHour(historyId: number, hours: IProfessionalHistoryHour[]): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + "/" + + historyId.toString() + "/Hour",
			hours,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}


export type tSchoolType = "IPEDS" | "NCES" | "Certificate" | "";

export interface ISchoolHistory {
	SchoolHistoryId?: string,
	SchoolId: string,
	SchoolType: tSchoolType,
	SchoolName: string,
	DateFrom: Date,
	DateTo?: Date,
	CurrentlyAttend?: boolean,
	CertificateId?: string,
	CIPCode?: string,
	Status?: string,
	FromMonth?: string,
	FromYear?: string,
	ToMonth?: string,
	ToYear?: string,
}

@Injectable({
	providedIn: 'root'
})
export class SchoolHistoryAPIService {
	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) { }
	private url = AppSettingService.appConfig.JourneyAPIUrl + "SchoolHistory";

	getSchoolHistory(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSchoolInHistory(id: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/" + encodeURIComponent(id),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveSchoolHistory(school: ISchoolHistory): Rx.Observable<any> {
		return this.http.post<any>(
			this.url,
			school,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	deleteSchoolHistory(historyId: string): Rx.Observable<any> {
		return this.http.delete<any>(
			this.url + "/" + historyId.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	hasSchoolHistory(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/HasSchool",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
}
